import React, { useEffect, useState, useRef } from "react";
import axios from "axios";

/**
 * PAID PDF POST-PURCHASE PAGE — /book-success?session_id=cs_test_...
 *
 * Verifies the Stripe Checkout Session server-side, then reveals a
 * JWT-signed 7-day download link. Fires GA4 + Meta Purchase ONCE on
 * verify success (never on page load), with zero PII in the payload.
 * noindex,nofollow.
 */

const BRAND = {
  deep: "#042F2E",
  teal: "#068fa2",
  gold: "#F4C36A",
  goldText: "#1B6B6E",
  cream: "#efeae7",
  mint: "#5EEAD4",
};

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_RETRIES = 8;

export default function BookSuccess() {
  const [state, setState] = useState("verifying"); // verifying | ready | error
  const [downloadUrl, setDownloadUrl] = useState("");
  const [journalUrl, setJournalUrl] = useState("");
  const [email, setEmail] = useState("");
  const [txnId, setTxnId] = useState("");
  const firedRef = useRef(false);

  useEffect(() => {
    document.title = "Your book is here · Temple Franklin";
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setState("error");
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const firePurchase = (value, transactionId) => {
      if (firedRef.current) return;
      const dedupeKey = `purchase_fired_${transactionId}`;
      try {
        if (window.sessionStorage && sessionStorage.getItem(dedupeKey)) {
          firedRef.current = true;
          return;
        }
      } catch (_) { /* storage guard */ }
      firedRef.current = true;
      const v = value && value > 0 ? value : 9.99;
      try {
        if (typeof window.gtag === "function") {
          window.gtag("event", "purchase", {
            transaction_id: transactionId,
            value: v,
            currency: "USD",
            items: [{
              item_id: "i_get_to_pdf_edition",
              item_name: "I GET TO — PDF Edition",
              price: v,
              quantity: 1,
            }],
          });
        }
        if (typeof window.fbq === "function") {
          window.fbq("track", "Purchase", {
            value: v,
            currency: "USD",
            content_ids: ["i_get_to_pdf_edition"],
            content_name: "I GET TO — PDF Edition",
            content_type: "product",
          });
        }
        if (typeof window.pintrk === "function") {
          window.pintrk("track", "checkout", {
            value: v,
            order_quantity: 1,
            currency: "USD",
            line_items: [{ product_name: "I GET TO — PDF Edition", product_id: "i_get_to_pdf_edition", product_price: v }],
          });
        }
      } catch (_) { /* analytics guard */ }
      try {
        if (window.sessionStorage) sessionStorage.setItem(dedupeKey, "1");
      } catch (_) { /* storage guard */ }
    };

    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/pdf/verify-session`, {
          params: { session_id: sessionId },
        });
        if (cancelled) return;
        if (res.data && res.data.download_url) {
          setDownloadUrl(`${API_URL}${res.data.download_url}`);
          if (res.data.journal_download_url) {
            setJournalUrl(`${API_URL}${res.data.journal_download_url}`);
          }
          setEmail(res.data.email || "");
          setTxnId(res.data.transaction_id || sessionId);
          firePurchase(res.data.value, res.data.transaction_id || sessionId);
          setState("ready");
          return;
        }
      } catch (_) { /* retry below */ }
      attempts += 1;
      if (attempts >= MAX_RETRIES) {
        if (!cancelled) setState("error");
        return;
      }
      setTimeout(poll, 2000);
    };

    poll();

    return () => {
      cancelled = true;
      const stale = document.querySelector('meta[name="robots"]');
      if (stale) stale.remove();
    };
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-16"
      style={{ background: `linear-gradient(160deg, ${BRAND.deep} 0%, ${BRAND.teal} 100%)`, color: BRAND.cream }}
      data-testid="book-success-page"
    >
      <div className="max-w-2xl w-full text-center">
        <p className="font-sans text-xs tracking-[0.4em] uppercase mb-5" style={{ color: BRAND.gold }}>
          Tapped In Publishing ✦
        </p>

        {state === "verifying" && (
          <div data-testid="book-success-verifying">
            <h1 className="font-serif tracking-tight leading-[1.05] mb-6" style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)" }}>
              Confirming your payment…
            </h1>
            <div
              className="inline-block w-10 h-10 rounded-full animate-spin mt-2"
              style={{ border: `3px solid ${BRAND.mint}33`, borderTopColor: BRAND.mint }}
              aria-label="Loading"
            />
            <p className="font-serif italic text-lg mt-8" style={{ color: `${BRAND.cream}cc` }}>
              This takes just a moment — hang tight.
            </p>
          </div>
        )}

        {state === "ready" && (
          <div data-testid="book-success-ready">
            <h1
              className="font-serif tracking-tight leading-[1.05] mb-7"
              style={{ fontSize: "clamp(2.2rem, 5vw, 3.6rem)" }}
              data-testid="book-success-headline"
            >
              Welcome home, dear one.
              <br />
              <span style={{ color: BRAND.mint }} className="italic">Your book is here.</span>
            </h1>

            <div className="flex justify-center mb-8">
              <a
                href={downloadUrl}
                download
                className="inline-flex items-center gap-3 px-9 py-5 rounded-full font-sans font-semibold tracking-wide transition-transform hover:-translate-y-0.5 hover:shadow-2xl"
                style={{ background: BRAND.gold, color: BRAND.goldText, fontSize: "17px" }}
                data-testid="book-success-download-btn"
              >
                Download your book (PDF)
                <span style={{ fontSize: "20px" }}>↓</span>
              </a>
            </div>

            <p className="font-serif italic text-base md:text-lg leading-relaxed mb-8 max-w-xl mx-auto" style={{ color: `${BRAND.cream}dd` }}>
              {email ? <>Your receipt was sent to <strong>{email}</strong>. </> : null}
              This download link is valid for 7 days — save it now if you're not sure you'll be able to click again.
            </p>

            {journalUrl && (
              <div
                className="max-w-xl mx-auto mt-10 mb-8 rounded-2xl px-7 py-8 text-center"
                style={{ background: `${BRAND.mint}14`, border: `1px solid ${BRAND.mint}33` }}
                data-testid="companion-journal-section"
              >
                <h2
                  className="font-serif tracking-tight leading-[1.1] mb-4"
                  style={{ fontSize: "clamp(1.5rem, 4vw, 2.2rem)", color: BRAND.cream }}
                  data-testid="companion-journal-headline"
                >
                  One more thing, Lovely —
                </h2>
                <p className="font-serif italic text-base md:text-lg leading-relaxed mb-7 max-w-lg mx-auto" style={{ color: `${BRAND.cream}dd` }}>
                  The companion journal is yours. Free. It was made to sit beside the book — print it at home, built for a real pen and a real cup of tea.
                </p>
                <a
                  href={journalUrl}
                  download
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-sans font-semibold tracking-wide transition-transform hover:-translate-y-0.5 hover:shadow-2xl"
                  style={{ background: "#F4C36A", color: "#1B6B6E", fontSize: "16px" }}
                  data-testid="companion-journal-download-btn"
                >
                  Download the Companion Journal
                  <span style={{ fontSize: "18px" }}>↓</span>
                </a>
                <p className="font-serif italic text-sm mt-5" style={{ color: `${BRAND.cream}bb` }} data-testid="companion-journal-signoff">
                  Welcome home. — Temple
                </p>
              </div>
            )}

            <p className="mb-8">
              <a
                href="/build-your-own"
                className="font-serif italic text-lg underline underline-offset-4 hover:opacity-80 transition-opacity"
                style={{ color: BRAND.mint }}
                data-testid="book-success-course-link"
              >
                Ready to build your own? The 23-lesson course — $33
              </a>
            </p>

            <p className="font-sans text-xs tracking-wide" style={{ color: `${BRAND.cream}88` }} data-testid="book-success-txn">
              Purchased from Tapped In Publishing · Transaction {txnId ? txnId.slice(-8) : "—"}
            </p>
          </div>
        )}

        {state === "error" && (
          <div data-testid="book-success-error">
            <h1 className="font-serif tracking-tight leading-[1.05] mb-6" style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)" }}>
              We're still confirming your payment.
            </h1>
            <p className="font-serif italic text-lg leading-relaxed mb-8 max-w-xl mx-auto" style={{ color: `${BRAND.cream}dd` }}>
              Your payment may still be processing. Refresh this page in a moment — or email{" "}
              <a href="mailto:mindbodyspirithygiene@gmail.com" className="underline underline-offset-4" style={{ color: BRAND.mint }}>
                mindbodyspirithygiene@gmail.com
              </a>{" "}
              and Temple will send your download straight to you.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-sans font-semibold tracking-wide transition-transform hover:-translate-y-0.5"
              style={{ background: BRAND.gold, color: BRAND.goldText, fontSize: "15px" }}
              data-testid="book-success-retry-btn"
            >
              Try again
            </button>
          </div>
        )}

        <a
          href="/"
          className="inline-block mt-10 font-sans text-sm tracking-wide underline-offset-4 hover:underline"
          style={{ color: `${BRAND.cream}aa` }}
          data-testid="book-success-home-link"
        >
          ← Back to mindbodyspirithygienetools.com
        </a>
      </div>
    </div>
  );
}
